// Skeleton — the loading-placeholder primitive, pure.
//
// A quiet block that stands in for content that hasn't arrived yet. Built on
// foundations v1 against the design vision (.claude/research/design/06): the fill is the
// neutral subtle ground (--color-bg-subtle) with a soft ambient shimmer — no
// border, no accent (the accent is reserved for the primary action / the
// agent, never spent on chrome like this).
//
// DECORATIVE, not announced. The element is `aria-hidden` because a skeleton
// carries no information a screen-reader user needs — the loading STATE is
// announced once, by a live region that lives at the fetch boundary (the
// impure shell), not on each placeholder tile. Spraying aria-busy/role=status
// across dozens of tiles would spam assistive tech; one region up top is the
// SC 4.1.3 pattern.
//
// Pure/props-driven: `variant` + `radius` pick the shape; `width`/`height`
// size it. Nothing here fetches, times out, or knows what it's standing in
// for — the owner renders Skeletons while its data is pending and swaps them
// for real content when it lands. No side effects, no state.
//
// Two variants (distinct shapes, not a flag): `text` defaults to a single
// line's height, `block` to a rectangular region. Both defaults are
// overridable via `width`/`height`; a number is treated as px, a string is
// passed through (so "8rem", "60%", "var(--space-16)" all work).

import type { CSSProperties } from "react";
import "./Skeleton.css";

export type SkeletonVariant = "text" | "block";
type SkeletonRadius = "sm" | "md" | "full";

export type SkeletonProps = {
    readonly variant?: SkeletonVariant;
    readonly width?: string | number;
    readonly height?: string | number;
    readonly radius?: SkeletonRadius;
    readonly className?: string;
};

// A bare number is a pixel length; a string is a caller-supplied CSS length
// (kept verbatim). `undefined` means "let the variant's CSS default stand".
const toLength = (value: string | number | undefined): string | undefined =>
    typeof value === "number" ? `${value}px` : value;

export const Skeleton = ({
    variant = "text",
    width,
    height,
    radius,
    className,
}: SkeletonProps) => {
    // Radius follows the variant by default (chips/inline → sm, blocks → md),
    // and is overridden when the caller asks for one (e.g. `full` for avatars).
    const resolvedRadius = radius ?? (variant === "text" ? "sm" : "md");
    const style: CSSProperties = {
        width: toLength(width),
        height: toLength(height),
    };
    return (
        <div
            aria-hidden="true"
            className={`bilda-skeleton bilda-skeleton--${variant} bilda-skeleton--${resolvedRadius}${
                className ? ` ${className}` : ""
            }`}
            style={style}
        />
    );
};
